import { useT } from '@transifex/react'
import { useAppDispatch, useAppSelector } from '../../../../state/hooks'
import {
  selectIsSubmittedSaleEarly,
  selectSaleId,
  selectSaleNote,
  setHasUpdateNoteOnConfirmation,
  setSaleNote,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import { useRegisterManager, useSaleManager } from '../../../../ng-compat/hooks'

export interface NotesProps {
  onDeleted: () => void
  shouldFocus: boolean
}

export interface NotesModel {
  note: string
  label: string
  supplementaryLabel: string
  placeholder: string
  deleteLabel: string
  shouldFocus: boolean
  onChange: (value: string) => void
  onBlur: () => void
  onDelete: () => void
}

export const useNotesModel = (props: NotesProps): NotesModel => {
  const t = useT()
  const dispatch = useAppDispatch()
  const note = useAppSelector(selectSaleNote)
  const saleID = useAppSelector(selectSaleId)
  const isSubmittedEarly = useAppSelector(selectIsSubmittedSaleEarly)
  const saleManager = useSaleManager()
  const registerManager = useRegisterManager()

  const saveNote = (updatedNote: string) => {
    dispatch(setSaleNote(updatedNote))

    if (!saleID && isSubmittedEarly) {
      dispatch(setHasUpdateNoteOnConfirmation(true))
    }

    saleManager.saveSale()
  }

  const shouldPrintNoteOnReceipt = !!registerManager.getActiveRegister()
    ?.print_note_on_receipt

  const label = shouldPrintNoteOnReceipt
    ? t('Note', {
        _context: 'Sale note label for the sale note input',
      })
    : t('Staff Note', {
        _context:
          'Sale note label for the sale note input when it will not be printed on the receipt',
      })

  const supplementaryLabel = shouldPrintNoteOnReceipt
    ? t('Customers will be able to see this note on their receipt', {
        _context: 'Supplementary label to the sale notes input',
      })
    : t('Customers will not see this note on their receipt', {
        _context: 'Supplementary label to the sale notes input',
      })

  const placeholder = t('Enter a note about this sale', {
    _context: 'sale note placeholder',
  })
  const deleteLabel = t('Delete sale note', {
    _context: 'Aria label for the delete sale note trash icon',
  })

  return {
    note,
    label,
    supplementaryLabel,
    placeholder,
    deleteLabel,
    shouldFocus: props.shouldFocus,
    onChange: value => dispatch(setSaleNote(value)),
    onBlur: () => saveNote(note),
    onDelete: () => {
      saveNote('')
      props.onDeleted()
    },
  }
}
